import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FailedWorkItemExceptionType,
  WorkItemState,
} from '@robocorp/robocloud-orchestrator-interface';
import {
  Column,
  DateRange,
  Dialog,
  Filter,
  FilterGroup,
  SkeletonLoader,
  SortDirection,
  Table,
  TableSkeleton,
} from '@robocorp/components';
import { styled } from '@robocorp/theme';
import {
  ConfirmDialog,
  ConfirmProps,
  FilterSettings,
  TimeRangePeriod,
  useCanWriteWorkItemData,
  useDataExplorer,
  useFilterSettings,
  useFilterTimeRangeSettings,
  useTimeRange,
} from '~/hooks';
import {
  defaultWorkItemsData,
  useDeleteWorkItemDataMutation,
  useDeleteWorkItemsMutation,
  useDiscardWorkItemsMutation,
  useProcessActivitiesQuery,
  useRetryWorkItemsMutation,
  useWorkItemsLazyQuery,
} from '~/queries';
import { TimeRangeValues } from '~/constants/timeRanges';
import { getTimeRangeParams } from '~/utils/timeRangeHelpers';
import { FilterSearch, FilterSettingsCompactMenu } from '~/components/filters';
import { TimeRangeDialog } from '~/components';
import {
  ExternalWorkItemShortState,
  getExternalWorkItemState,
  getInternalWorkItemShortStates,
} from '~/utils/mappingHelper';
import WorkDataTableRow from './WorkDataTableRow';
import { SelectFilter } from '../SelectFilter';

const exceptionTypeOptions = [
  FailedWorkItemExceptionType.Application,
  FailedWorkItemExceptionType.Business,
  FailedWorkItemExceptionType.Orchestrator,
  FailedWorkItemExceptionType.Unspecified,
];

type Props = {
  total: number;
  processId: string;
  pageSize?: FilterSettings['size'];
};

const defaultSortState: [string, SortDirection] = ['createTs', 'desc'];
const defaultPreset = TimeRangeValues.ALL_TIME;

const Actions = styled(Dialog.Actions)`
  padding: ${({ theme }) => theme.space.$16};
`;

const Content: FC<Props> = ({ total, processId, pageSize = 25 }) => {
  const { t } = useTranslation();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmProps | null>(null);

  const [openDateRangeDialog, setOpenDateRangeDialog] = useState<boolean>(false);
  const [, config] = useDataExplorer();
  const { scope, activityRunId, pRunId: processRunId = '' } = config;

  const canWriteWorkItemData = useCanWriteWorkItemData();

  const columnDefinition = useMemo<Column[]>(
    () => [
      {
        id: 'id',
        title: t('id'),
        hidden: true,
        width: 180,
        ellipsis: true,
        minWidth: 180,
      },
      {
        id: 'state',
        title: t('state'),
        required: true,
        ellipsis: true,
        sortable: true,
        minWidth: 140,
      },
      {
        id: 'exceptionMessage',
        title: t('exceptionMessage'),
        groupName: t('exceptions'),
        minWidth: 160,
        ellipsis: true,
      },
      {
        id: 'exceptionType',
        title: t('exceptionType'),
        groupName: t('exceptions'),
        minWidth: 160,
        ellipsis: true,
        hidden: true,
      },
      {
        id: 'exceptionCode',
        title: t('exceptionCode'),
        groupName: t('exceptions'),
        hidden: true,
        minWidth: 160,
        ellipsis: true,
      },
      {
        id: 'processRun',
        title: t('processRun.title'),
        groupName: t('general'),
        required: true,
        minWidth: 240,
        ellipsis: true,
      },
      {
        id: 'activityRun',
        title: t('activityRun'),
        groupName: t('general'),
        minWidth: 160,
        ellipsis: true,
      },
      {
        id: 'workData',
        title: t('data'),
        groupName: t('data'),
        minWidth: 100,
        ellipsis: true,
      },
      {
        id: 'filesCount',
        title: t('files.title'),
        groupName: t('data'),
        minWidth: 80,
        width: 80,
        ellipsis: true,
      },
      {
        id: 'createTs',
        title: t('created'),
        groupName: t('time'),
        minWidth: 160,
        ellipsis: true,
        sortable: true,
      },
      {
        id: 'stateTs',
        title: t('updated'),
        groupName: t('time'),
        minWidth: 160,
        ellipsis: true,
        sortable: true,
      },
      {
        id: 'openPanel',
        title: '',
        width: 60,
        minWidth: 60,
        resizable: false,
        align: 'center',
      },
      {
        id: 'actions',
        title: t('options'),
        width: 60,
        minWidth: 60,
        align: 'center',
        resizable: false,
      },
    ],
    [t],
  );

  const {
    columns,
    columnVisibility,
    onColumnVisibilityChange,
    resize,
    onResizeChange,
    page,
    cursor,
    onCursorChange,
    size,
    onSizeChange,
    search,
    searchDebounced,
    onSearchChange,
    filters,
    sort: sortState,
    onSortChange,
    selected,
    onSelectChange,
    onFilterChange,
  } = useFilterSettings<{
    exceptionType: typeof exceptionTypeOptions;
    state: ExternalWorkItemShortState[];
    activityId: string[];
    period: TimeRangePeriod;
  }>('workItems', {
    columns: columnDefinition,
    filters: {
      exceptionType: [],
      state: [],
      activityId: [],
      period: [defaultPreset],
    },
    sort: defaultSortState,
    size: pageSize,
  });

  const [filterTimeRangeProps, stringifyFilterTimeRange] = useFilterTimeRangeSettings(
    filters.period?.[0] ?? defaultPreset,
    filters.period?.[1],
  );

  const { preset, range, rangeLabel, onPresetChange, getPresets } =
    useTimeRange(filterTimeRangeProps);
  const presets = useMemo(() => getPresets(), [getPresets]);

  const combinedFilters = useMemo(
    () => ({
      ...filters,
      exceptionType: filters?.exceptionType ?? [],
      activityId: filters?.activityId ?? [],
      state: filters?.state ?? [],
      period: [preset],
    }),
    [filters, preset],
  );

  const from = page * size;
  const sort = sortState || defaultSortState;
  const [sortBy, sortOrder] = sort ?? [];

  const exceptionType = combinedFilters.exceptionType[0];
  const isCustomPeriod = preset === TimeRangeValues.CUSTOM;

  const { mutateAsync: onRetryWorkItem } = useRetryWorkItemsMutation();
  const { mutateAsync: onDiscardWorkItem } = useDiscardWorkItemsMutation();
  const { mutateAsync: onDeleteWorkItemDataAsync } = useDeleteWorkItemDataMutation();
  const { mutateAsync: onDeleteWorkItemAsync } = useDeleteWorkItemsMutation();
  const { data: processSteps = [], isLoading: processActivitiesIsLoading } =
    useProcessActivitiesQuery(processId);

  const processStepsByStepId = useMemo(
    () =>
      processSteps.reduce<Record<string, (typeof processSteps)[number]>>((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {}),
    [processSteps],
  );

  const onDeleteWorkItem = useCallback(
    (args: { processId: string; workItemIds: string[] }) =>
      setConfirmDialog({
        onConfirm: () => {
          onDeleteWorkItemAsync(args);
          setConfirmDialog(null);
        },
        title: t('deleteWorkItem'),
        text: t('deleteWorkItemText'),
      }),
    [onDeleteWorkItemAsync, t],
  );

  const onDeleteWorkItemData = useCallback(
    ({ workItemId }: { workItemId: string }) =>
      setConfirmDialog({
        onConfirm: () => {
          onDeleteWorkItemDataAsync({ workItemId });
          setConfirmDialog(null);
        },
        title: t('deleteWorkItemData'),
        text: t('deleteWorkItemDataText'),
      }),
    [onDeleteWorkItemDataAsync, t],
  );

  const [
    {
      data: {
        data: workItems,
        total: filteredTotal,
        previousCursor,
        nextCursor,
      } = defaultWorkItemsData,
      isLoading: isLoadingWorkItems,
      isFetched: isFetchedWorkItem,
    },
    getWorkItems,
  ] = useWorkItemsLazyQuery();

  const workItemQueryParams = useMemo(
    () => ({
      processId,
      filters: {
        activityId: combinedFilters.activityId,
        activityRunId,
        state: getInternalWorkItemShortStates(combinedFilters.state),
      },
      queryArgs: {
        size,
        cursor,
        sortBy,
        sortOrder,
        exceptionType,
        search: searchDebounced,
        ...getTimeRangeParams('createTs', range),
      },
    }),
    [
      activityRunId,
      combinedFilters.state,
      combinedFilters.activityId,
      cursor,
      exceptionType,
      processId,
      range,
      searchDebounced,
      size,
      sortBy,
      sortOrder,
    ],
  );

  useEffect(() => {
    if (scope === 'process') {
      getWorkItems({ ...workItemQueryParams, scope });
    }

    if (scope === 'processRun') {
      getWorkItems({ ...workItemQueryParams, processRunId, scope });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processRunId, scope, workItemQueryParams]);

  const filterOptions = useMemo<Record<string, FilterGroup>>(
    () => ({
      exceptionType: {
        label: t('exceptionType'),
        searchable: true,
        closeMenuOnItemSelect: true,
        options: exceptionTypeOptions.map((value) => ({
          value,
          label: t(`workItemExceptionType.${value}`),
          itemType: 'radio',
        })),
      },
      state: {
        label: t('state'),
        searchable: true,
        closeMenuOnItemSelect: false,
        options: [
          WorkItemState.New,
          WorkItemState.Pending,
          WorkItemState.InProgress,
          WorkItemState.Failed,
          WorkItemState.Completed,
          WorkItemState.Output,
        ].map((value) => ({
          value: getExternalWorkItemState(value),
          label: t(`workItemState.${value}`),
          itemType: 'checkbox',
          shortcut: value === WorkItemState.Failed,
        })),
      },
      activityId: {
        label: t('step'),
        searchable: true,
        closeMenuOnItemSelect: false,
        options: processSteps
          .map((processStep) => ({
            value: processStep.id,
            label: processStep.name ?? (processStep as { displayName?: string }).displayName,
            itemType: 'checkbox' as const,
          }))
          .filter((option) => option.label !== undefined),
      },
      period: {
        label: t('period'),
        searchable: true,
        valueLabel: rangeLabel,
        closeMenuOnItemSelect: true,
        permanent: true,
        options: presets.map((option) => ({ ...option, itemType: 'radio' })),
      },
    }),
    [t, processSteps, rangeLabel, presets],
  );

  const onFilter = useCallback(
    (newValue: Record<string, string[]>) => {
      const { period: newPeriod, ...newFilters } = newValue;

      const newPreset = newPeriod?.[0] as TimeRangeValues;
      if (!newPreset || newPreset === TimeRangeValues.CUSTOM) {
        if (
          (!isCustomPeriod && newPreset === TimeRangeValues.CUSTOM) ||
          (!newPreset && isCustomPeriod)
        ) {
          setOpenDateRangeDialog(true);
        }
        return onFilterChange((previous) => ({ ...previous, ...newFilters }));
      }

      onPresetChange(newPreset);
      return onFilterChange((previous) => ({
        ...previous,
        ...newFilters,
        period: stringifyFilterTimeRange([newPreset], defaultPreset),
      }));
    },
    [onPresetChange, onFilterChange, isCustomPeriod, stringifyFilterTimeRange],
  );

  const onDateRange = useCallback(
    (dateRange: DateRange) => {
      onPresetChange(TimeRangeValues.CUSTOM, dateRange);
      return onFilterChange((previousFilters) => ({
        ...previousFilters,
        period: stringifyFilterTimeRange([TimeRangeValues.CUSTOM, dateRange]),
      }));
    },
    [onPresetChange, onFilterChange, stringifyFilterTimeRange],
  );

  const rowProps = useMemo(
    () => ({
      canWriteWorkItemData,
      processSteps: processStepsByStepId,
      onRetryWorkItem,
      onDiscardWorkItem,
      onDeleteWorkItem,
      onDeleteWorkItemData,
    }),
    [
      canWriteWorkItemData,
      processStepsByStepId,
      onDeleteWorkItem,
      onDeleteWorkItemData,
      onDiscardWorkItem,
      onRetryWorkItem,
    ],
  );

  return (
    <>
      <Dialog.Content>
        {selected.length > 0 ? (
          <SelectFilter
            processId={processId}
            selected={selected}
            onSelectChange={onSelectChange}
            workItems={workItems}
            total={filteredTotal}
            filters={workItemQueryParams}
            filterOptions={filterOptions}
          />
        ) : (
          <Filter
            contentBefore={<FilterSearch search={search} onSearchChange={onSearchChange} />}
            contentAfter={
              <FilterSettingsCompactMenu
                columns={columns}
                visibility={columnVisibility}
                onVisibilityChange={onColumnVisibilityChange}
                size={size}
                sizes={[25, 50, 100]}
                onSizeChange={onSizeChange}
                sort={sort}
                onSort={onSortChange}
              />
            }
            options={filterOptions}
            values={combinedFilters}
            onChange={onFilter}
            resetLabel={t('filter.reset')}
            hint={
              filteredTotal !== undefined
                ? t('filter.resultHint', {
                    count: filteredTotal,
                    total: Math.max(filteredTotal, total),
                    label: t('workItems').toLowerCase(),
                  })
                : undefined
            }
          />
        )}

        <SkeletonLoader
          loading={processActivitiesIsLoading || isLoadingWorkItems}
          skeleton={TableSkeleton}
        >
          <Table
            columns={columns}
            rowCount="all"
            data={workItems}
            rowProps={rowProps}
            row={WorkDataTableRow}
            noDataFound={t('noWorkData')}
            selected={selected}
            onSelect={onSelectChange}
            selectable
            sort={sort}
            onSort={onSortChange}
            resize={resize}
            onResize={onResizeChange}
            sticky={['background.panel']}
          />
        </SkeletonLoader>
      </Dialog.Content>
      <Actions>
        <Table.Pagination
          totalEntries={filteredTotal}
          previousButtonDisabled={!previousCursor || !isFetchedWorkItem}
          nextButtonDisabled={!nextCursor || !isFetchedWorkItem}
          pageStart={from}
          pageEnd={Math.min(from + size, filteredTotal)}
          nextPage={() => onCursorChange(nextCursor, 'next')}
          previousPage={() => onCursorChange(previousCursor, 'previous')}
        />
      </Actions>
      <TimeRangeDialog
        title={t('custom')}
        isOpen={openDateRangeDialog}
        onClose={() => setOpenDateRangeDialog(false)}
        dateRange={range}
        onSelect={onDateRange}
      />
      <ConfirmDialog
        {...confirmDialog}
        onClose={() => setConfirmDialog(null)}
        open={confirmDialog !== null}
      />
    </>
  );
};

export const WorkDataTable = memo(Content);
